import dayjs from "dayjs";
import type { TFunction } from "i18next";

import {
  ComputedDataKey,
  DEFAULT_PASSENGER_SEATS_COUNT,
  errors,
  NO_VALUE_DISPLAY,
  PASSENGER_RIDE_SITE_MISMATCH_EMOJI,
  RideComputedDataKeys,
  SECONDS_IN_MINUTE,
} from "../../constants";
import {
  createErrorResultExt,
  type IPassengerInfo,
  type IRideDto,
  type IRideMetadata,
  type IRideTableDto,
  type IStationInfo,
  type IStationInfoBase,
  type SimpleResult,
  type TStationComposedId,
} from "../../dto";
import {
  BoardingStateEnum,
  DirectionEnum,
  RideStationType,
  RideStatusEnum,
  RideTypeCategoryEnum,
  TimeZoneEnum,
  UserEditDisplayModeEnum,
} from "../../enums";
import type {
  ILocationInfo,
  IPassengerMove,
  IRideAreasSnapshot,
  IRideRoute,
  IRideStationsBulkUpdateParams,
  IRideStationsBulkUpdateRecountData,
  RideUpdateDriverOrVehicleFieldName,
  SoftValidationErrorAdditionalValues,
  TTranslationParams,
} from "../../models";
import {
  areArraysExactlySame,
  insertBeforeLast,
  unique,
} from "../array.helpers";
import {
  addSeconds,
  equalDates,
  isValidDateTime,
  nowTDateISO,
  timeDiffMinutesCeiling,
  timeDiffSeconds,
} from "../date.helpers";
import { Log } from "../log.helpers";
import {
  getRealRouteEndpoints,
  isRoutingWaypointDragOrderable,
  isStationRouteWaypointStation,
} from "../station-route";
import { combineStrings } from "../string.helpers";
import {
  extractStationId,
  getStationComposedId,
  stationComposedIdMatch,
} from "./ride-station-composed-id.helpers";
import { rebaseStationsArrivalTimesToUtcDate } from "./ride-stations-date-shift.helpers";
import { isPastTaxiSupplierRide } from "./ride-taxi-supplier.helpers";
import { sortStationsByPassengers } from "./rides.add-passenger.helpers";
import { rideShouldCleanupEmptyStations } from "./rides.fixed.helpers";
import { formatPassengerTooltipEntityWithId } from "./rides.passenger-tooltip.helpers";
import { normalizeRideMetadata } from "./rides.recount.helpers";

export const computeRideStationType = (
  pass: IPassengerInfo[],
  stationId: number,
): RideStationType => {
  const hasIn = pass.some(x => x.from === stationId);
  const hasOut = pass.some(x => x.to === stationId);

  if (hasIn && hasOut) {
    return RideStationType.Both;
  }

  if (hasIn) {
    return RideStationType.In;
  }

  if (hasOut) {
    return RideStationType.Out;
  }

  return RideStationType.None;
};

export const computeRideStationTypeSingle = (
  pass: IPassengerInfo,
  stationId: number,
): RideStationType => {
  const hasIn = pass.from === stationId;
  const hasOut = pass.to === stationId;
  const catchARide = pass?.catchARide;

  if (hasIn && hasOut) {
    return RideStationType.Both;
  }

  if (hasIn) {
    if (catchARide) {
      return RideStationType.CatchRideIn;
    }

    return RideStationType.In;
  }

  if (hasOut) {
    if (catchARide) {
      return RideStationType.CatchRideOut;
    }

    return RideStationType.Out;
  }

  return RideStationType.None;
};

export const getRideDtoStations = (
  ride: IRideDto | undefined,
): IStationInfo[] => {
  if (!ride) {
    return [];
  }

  return ride?.metadata?.stationsInfo?.map(station => station) || [];
};

export const getRideStationById = ({
  rideDto,
  stationComposedId,
}: {
  rideDto: Partial<IRideDto>;
  stationComposedId: TStationComposedId | undefined;
}): IStationInfo | undefined => {
  if (!stationComposedId || !rideDto?.metadata?.stationsInfo?.length) {
    return undefined;
  }

  return rideDto?.metadata?.stationsInfo?.find(x =>
    stationComposedIdMatch({ station: x, composedId: stationComposedId }),
  );
};

export const getRidePassengerDisplayStationId = (
  passenger: IPassengerInfo,
  direction: DirectionEnum | undefined,
): number | undefined => {
  return direction === DirectionEnum.DropOff ? passenger.to : passenger.from;
};

export const getRidePassengerOppositeRideStationId = (
  passenger: Pick<IPassengerInfo, "from" | "to">,
  currentStationId?: number,
): number | undefined => {
  if (!currentStationId) {
    return undefined;
  }

  const { from, to } = passenger;
  if (from === currentStationId && to && to !== currentStationId) {
    return to;
  }
  if (to === currentStationId && from && from !== currentStationId) {
    return from;
  }

  return undefined;
};

export const isPassengerRideSiteMismatch = ({
  passengerSiteId,
  rideSiteId,
}: {
  passengerSiteId?: number | null;
  rideSiteId?: number | null;
}): boolean => {
  if (!passengerSiteId || !rideSiteId) {
    return false;
  }

  return passengerSiteId !== rideSiteId;
};

export const getRidePassengerStationName = (
  passengerId: number,
  rideDto: Partial<IRideDto>,
): string => {
  const stations = rideDto.metadata?.stationsInfo;
  const passenger = rideDto.metadata?.passengersInfo?.find(
    p => p.id === passengerId,
  );

  if (!passenger || !stations?.length) {
    return NO_VALUE_DISPLAY;
  }

  const stationId = getRidePassengerDisplayStationId(
    passenger,
    rideDto.direction,
  );
  return stations.find(s => s.id === stationId)?.name || NO_VALUE_DISPLAY;
};

export const getRidePassengerStationTime = (
  passengerId: number,
  rideDto: Partial<IRideDto>,
): TDateISO | undefined => {
  const stations = rideDto.metadata?.stationsInfo;
  const passenger = rideDto.metadata?.passengersInfo?.find(
    p => p.id === passengerId,
  );

  if (!passenger || !stations?.length) {
    return undefined;
  }

  const stationId = getRidePassengerDisplayStationId(
    passenger,
    rideDto.direction,
  );
  return stations.find(s => s.id === stationId)?.arrivalTime;
};

export const getRideStationDuration = (
  rideDto: Partial<IRideDto> | undefined,
  ind: number,
): number | undefined => {
  const stations = rideDto?.metadata?.stationsInfo;

  if (!stations?.length || ind >= stations.length) {
    return undefined;
  }

  const timeA = stations[ind]?.arrivalTime;
  const timeB = stations[ind + 1]?.arrivalTime;

  if (!timeA || !timeB) {
    return undefined;
  }

  return timeDiffMinutesCeiling(timeB, timeA);
};

export interface IRideStationDurationDetails {
  previousStationTime?: TDateISO;
  nextStationTime?: TDateISO;
  durationSeconds?: number;
  durationMinutes?: number;
}

export const getRideStationDurationDetails = (
  rideDto: Partial<IRideDto> | undefined,
  ind: number,
): IRideStationDurationDetails => {
  const stations = rideDto?.metadata?.stationsInfo;

  if (!stations?.length || ind >= stations.length) {
    return {};
  }

  const previousStationTime = stations[ind]?.arrivalTime;
  const nextStationTime = stations[ind + 1]?.arrivalTime;

  if (!previousStationTime || !nextStationTime) {
    const partialDetails: IRideStationDurationDetails = {
      previousStationTime,
      nextStationTime,
    };
    return partialDetails;
  }

  const durationSeconds = timeDiffSeconds(nextStationTime, previousStationTime);
  const durationMinutes = Math.floor(durationSeconds / SECONDS_IN_MINUTE);

  const details: IRideStationDurationDetails = {
    previousStationTime,
    nextStationTime,
    durationSeconds,
    durationMinutes,
  };
  return details;
};

export const sortRidesByDate = (rides: IRideTableDto[]): IRideTableDto[] => {
  if (!rides?.length) {
    return [];
  }

  return rides.sort((a, b) => {
    if (!a.rideStart || !b.rideStart) {
      return -1;
    }

    const date1dayjs = dayjs(a.rideStart);
    const date2dayjs = dayjs(b.rideStart);

    if (!isValidDateTime(date1dayjs) || !isValidDateTime(date2dayjs)) {
      Log.debug("Invalid ride date from api, ", a.rideStart, b.rideStart); // TODO: add translation solution/key? add as toast?
      return -1;
    }

    const date1 = +date1dayjs;
    const date2 = +date2dayjs;
    return date1 - date2;
  });
};

export const getRideRoute = (ride?: Partial<IRideDto>): ILocationInfo[] => {
  return (
    ride?.metadata?.stationsInfo?.map(stationInfo => {
      const filteredPassengers =
        ride?.metadata?.passengersInfo?.filter(
          passenger =>
            passenger.from === stationInfo.id ||
            passenger.to === stationInfo.id,
        ) || [];
      const dropOffPassengers = filteredPassengers.filter(
        passenger => passenger.to === stationInfo.id,
      );
      const passengerList = filteredPassengers.map(
        passenger => `${passenger.firstName} ${passenger.lastName}`,
      );
      const isRoutingWaypoint = Boolean(stationInfo.waypointCategory);

      const locationInfo: ILocationInfo = {
        ...stationInfo.locationInfo,
        stationId: isRoutingWaypoint ? undefined : stationInfo.id,
        addressName: stationInfo.name,
        fullAddress: combineStrings(
          stationInfo.name,
          stationInfo?.locationInfo?.fullAddress,
          " (",
          ")",
        ),
        waypointCategory: stationInfo.waypointCategory,
      };

      return {
        ...locationInfo,
        passengerList,
        fullPassengersDetails: filteredPassengers,
        arrivalTime: stationInfo.arrivalTime,
        isDropoff: dropOffPassengers.length > 0,
      };
    }) || []
  );
};

const extractRawCourseLineColorFromComputed = (computed: unknown): unknown => {
  if (
    !computed ||
    typeof computed !== "object" ||
    !(RideComputedDataKeys.courseLineColor in computed)
  ) {
    return undefined;
  }

  return (computed as Record<string, unknown>)[
    RideComputedDataKeys.courseLineColor
  ];
};

export const getRideRouteInfo = (rideDto: Partial<IRideDto>): IRideRoute => {
  const computed = rideDto[ComputedDataKey];
  const rawCourseColor = extractRawCourseLineColorFromComputed(computed);
  const courseLineColorTrimmed =
    typeof rawCourseColor === "string" ? rawCourseColor.trim() : "";
  const courseLineColor = courseLineColorTrimmed
    ? courseLineColorTrimmed
    : undefined;

  const rideRoute: IRideRoute = {
    locations: getRideRoute(rideDto),
    routePolyline: rideDto?.metadata?.routePolyline,
    rideId: rideDto?.id,
    rideStatus: rideDto?.status,
    direction: rideDto?.direction,
    courseId: rideDto.courseId ?? undefined,
    courseLineColor,
  };
  return rideRoute;
};

export const convertToPassengerMove = (
  passenger: IPassengerInfo,
): IPassengerMove => {
  const passengerMove: IPassengerMove = {
    id: passenger.id,
    fromStationId: passenger.from || 0,
    toStationId: passenger.to || 0,
    seats: passenger.seats || DEFAULT_PASSENGER_SEATS_COUNT,
    bookingId: passenger.bookingId,
    shiftHour: passenger.shiftHour,
  };
  return passengerMove;
};

export const mergeInsertedStationWithoutPassengers = ({
  rideStations,
  stationsToAdd,
}: {
  rideStations: IStationInfo[];
  stationsToAdd: IStationInfo[];
}): IStationInfo[] => {
  if (!stationsToAdd?.length) {
    return rideStations;
  }

  if (!rideStations?.length) {
    return [...stationsToAdd];
  }

  const lastExistingStation = rideStations[rideStations.length - 1];

  if (lastExistingStation.isLocked) {
    return insertBeforeLast(rideStations, stationsToAdd);
  }

  return [...rideStations, ...stationsToAdd];
};

export const bulkUpdateRidePassengersStations = (
  {
    rideDto,
    passengerIds,
    stationFromId,
    stationToId,
  }: IRideStationsBulkUpdateParams,
  { stationsInfos }: IRideStationsBulkUpdateRecountData,
): IRideMetadata => {
  if (!rideDto?.metadata || !passengerIds?.length) {
    return rideDto?.metadata || {};
  }

  const passengersInfo = rideDto?.metadata?.passengersInfo || [];
  const passengerIdsSet = new Set(passengerIds);

  const updatedPassengers = passengersInfo.map(x => {
    if (!passengerIdsSet.has(x.id)) {
      return x;
    }

    return {
      ...x,
      from: stationFromId ? stationFromId : x.from,
      to: stationToId ? stationToId : x.to,
    };
  });

  const stationsInfoSorted = sortStationsByPassengers({
    rideDto,
    stationsInfos,
    passengersInfos: updatedPassengers,
  });

  const updatedMetadata: IRideMetadata = {
    ...rideDto.metadata,
    passengersInfo: updatedPassengers,
    stationsInfo: stationsInfoSorted,
  };

  Log.debug("Bulk action: Metadata before normalization", updatedMetadata);
  const normalizedMetadata = normalizeRideMetadata(updatedMetadata, {
    cleanupEmptyStations: rideShouldCleanupEmptyStations(rideDto),
    direction: rideDto.direction || DirectionEnum.PickUp,
  });
  Log.debug("Bulk action: Normalized metadata ", normalizedMetadata);
  return normalizedMetadata;
};

export const checkStationsOrder = (
  passengerInfos: IPassengerInfo[],
  stationsIdsOrdered: TStationComposedId[],
): boolean => {
  if (!passengerInfos?.length || !stationsIdsOrdered?.length) {
    return true;
  }

  let hasInvalidStationOrder = true;
  passengerInfos.forEach(p => {
    const indexIn = stationsIdsOrdered.findIndex(
      s => extractStationId(s) === p.from,
    );
    const indexOut = stationsIdsOrdered.findIndex(
      s => extractStationId(s) === p.to,
    );

    if (indexOut < indexIn) {
      hasInvalidStationOrder = false;
    }
  });

  return hasInvalidStationOrder;
};

export const isRideUpdateDriverOrVehicleFieldName = (
  fieldName: string,
): fieldName is RideUpdateDriverOrVehicleFieldName =>
  fieldName === "driverId" || fieldName === "vehicleId";

export const filterRidesByUserChanged = ({
  resultRows,
  editedOnlyDisplayMode,
  currentUserId,
  timezone,
}: {
  resultRows: IRideTableDto[];
  editedOnlyDisplayMode: UserEditDisplayModeEnum;
  currentUserId: number | undefined;
  timezone: TimeZoneEnum;
}): IRideTableDto[] => {
  // Read once, not once per row in the predicate below.
  const nowIso = nowTDateISO(timezone);

  return resultRows?.filter(r => {
    switch (true) {
      case editedOnlyDisplayMode === UserEditDisplayModeEnum.AlteredRides:
        return !!r.updatedUserId;
      case editedOnlyDisplayMode === UserEditDisplayModeEnum.NotAlteredRides:
        return !r.updatedUserId;
      case editedOnlyDisplayMode === UserEditDisplayModeEnum.AlteredByMeRides:
        return r.updatedUserId === currentUserId;
      case editedOnlyDisplayMode ===
        UserEditDisplayModeEnum.AlteredByMeTodayRides:
        return (
          r.updatedUserId === currentUserId &&
          r.updatedAt &&
          equalDates(r.updatedAt, nowIso)
        );
      case editedOnlyDisplayMode ===
        UserEditDisplayModeEnum.AlteredByMeNotTodayRides:
        return (
          r.updatedUserId &&
          r.updatedUserId === currentUserId &&
          r.updatedAt &&
          !equalDates(r.updatedAt, nowIso)
        );
      case editedOnlyDisplayMode ===
        UserEditDisplayModeEnum.AlteredNotByMeTodayRides:
        return (
          r.updatedUserId !== currentUserId &&
          r.updatedAt &&
          equalDates(r.updatedAt, nowIso)
        );
      case editedOnlyDisplayMode ===
        UserEditDisplayModeEnum.AlteredNotByMeRides:
        return r.updatedUserId && r.updatedUserId !== currentUserId;
      case editedOnlyDisplayMode ===
        UserEditDisplayModeEnum.PassengerSelfRemoved:
        return r.metadata?.hasSelfRemovedPassengers === true;
      default:
        return true;
    }
  });
};

export const isRideGreyed = (
  rideDto: Partial<IRideDto> | undefined,
): boolean => {
  return (
    (Boolean(rideDto?.status) && rideDto?.status === RideStatusEnum.Canceled) ||
    // RYDE-7633: a past taxi-supplier ride is locked read-only, so it renders greyed/non-interactive
    // everywhere the stations & passenger controls funnel through this helper.
    isPastTaxiSupplierRide(rideDto)
  );
};

export const isSingleLastOrFirstStationWithPassengers = (
  rideDto: IRideDto | undefined,
  isFirstStation?: boolean,
  isLastStation?: boolean,
  station?: IStationInfo,
): boolean => {
  const allPassengers = rideDto?.metadata?.passengersInfo || [];

  if (!allPassengers?.length) {
    return false;
  }

  const stationPassengers = allPassengers?.filter(
    x => x.to === station?.id || x.from === station?.id,
  );

  if (!stationPassengers?.length) {
    return false;
  }

  const outStationIds = unique(
    allPassengers?.filter(x => x.to).map(x => x.to || 0) || [],
  );
  const inStationIds = unique(
    allPassengers?.filter(x => x.from).map(x => x.from || 0) || [],
  );

  if (isLastStation && outStationIds.length <= 1) {
    return true;
  }

  if (isFirstStation && inStationIds.length <= 1) {
    return true;
  }

  return false;
};

export const isStationDragOrderable = ({
  rideDto,
  isLocked,
  isFirstStation,
  isLastStation,
  station,
}: {
  rideDto: IRideDto | undefined;
  isLocked?: boolean;
  isFirstStation?: boolean;
  isLastStation?: boolean;
  station: IStationInfo;
}): boolean => {
  const isGreyed = isRideGreyed(rideDto);
  const isSingleLastOrFirstStation = isSingleLastOrFirstStationWithPassengers(
    rideDto,
    isFirstStation,
    isLastStation,
    station,
  );
  return (
    !isLocked &&
    !isSingleLastOrFirstStation &&
    !rideDto?.courseId &&
    !isGreyed &&
    !isStationRouteWaypointStation(station)
  );
};

export const isWaypointDragOrderable = ({
  rideDto,
  isLocked,
  station,
  isFirstStation,
  isLastStation,
}: {
  rideDto: Partial<IRideDto> | undefined;
  isLocked?: boolean;
  station: IStationInfo;
  isFirstStation?: boolean;
  isLastStation?: boolean;
}): boolean => {
  if (isRideGreyed(rideDto) || rideDto?.courseId) {
    return false;
  }

  return isRoutingWaypointDragOrderable({
    station,
    isLocked,
    isFirstStation,
    isLastStation,
    stations: rideDto?.metadata?.stationsInfo,
  });
};

export const calculatePassengersInfoSeatsCount = (
  passengersInfo: { seats?: number }[] | undefined,
): number => {
  if (!passengersInfo || !Array.isArray(passengersInfo)) {
    return 0;
  }

  return (
    passengersInfo
      ?.map(x => x.seats || 1)
      .reduce((sum, seats) => sum + Number(seats || 1), 0) || 0
  );
};

/**
 * Builds metadata.siteIds from passengers only (never ride.siteId).
 * Mirrors BE calculateRideSiteIds.
 *
 * Cases:
 * - passengers with sites → unique passenger site ids
 * - passengers without site → omitted (empty metadata.siteIds)
 * - no passengers → []
 *
 * Ride column site is ACL-filtered on BE separately (rideSiteAccessQuery).
 */
export const calculateRideSiteIds = (
  passengersInfo: { siteId?: number | null }[] | undefined,
): number[] => {
  if (!passengersInfo?.length) {
    return [];
  }

  return unique(
    passengersInfo
      .filter(passenger => passenger.siteId && Number(passenger.siteId))
      .map(passenger => Number(passenger.siteId)),
  );
};

export const rideDragCountSelectedIds = (
  rideDto: Partial<IRideDto> | undefined,
  dragSelectedIds: number[] | undefined,
  curPassengerId?: number,
): number => {
  const dragSelectedIdsSet = dragSelectedIds ? new Set(dragSelectedIds) : null;
  const passengers = rideDto?.metadata?.passengersInfo?.filter(
    x => dragSelectedIdsSet?.has(x.id) || curPassengerId === x.id,
  );
  return calculatePassengersInfoSeatsCount(passengers || []);
};

export const swapPassengerDirections = (passenger: IPassengerInfo): void => {
  [passenger.from, passenger.to] = [passenger.to, passenger.from];
  passenger.shiftHour = undefined;
};

export const reverseStationsOrder = (
  ride: IRideDto,
  timezone: TimeZoneEnum = TimeZoneEnum.AsiaJerusalem,
): IRideDto => {
  const originalDirection = ride.direction;
  const originalRide: IRideDto = { ...ride };
  const direction =
    originalDirection === DirectionEnum.PickUp
      ? DirectionEnum.DropOff
      : DirectionEnum.PickUp;

  const stations = ride.metadata?.stationsInfo || [];
  const stationsIdsReversed = ride.metadata?.stationIds?.toReversed() || [];
  const passengersInfoReversed =
    ride.metadata?.passengersInfo?.toReversed() || [];
  const passengerIdsReversed = ride.metadata?.passengerIds?.toReversed() || [];

  const stationsReversed = (stations?.toReversed() || []).map(x => ({ ...x })); // unblock recoil freeze
  const distancesReversed = ride.metadata?.distances?.toReversed() || [];
  const durations: number[] = [];

  for (let i = 0; i < stations.length - 1; i++) {
    const durationSeconds = timeDiffSeconds(
      stations[i].arrivalTime,
      stations[i + 1].arrivalTime,
    );
    durations[i] = durationSeconds;
  }

  const durationsReversed = durations.toReversed() || [];

  // Recalculate arrival times for reversed stations
  if (stationsReversed.length) {
    for (let i = 0; i < stationsReversed.length - 1; i++) {
      const arrivalCur = stationsReversed[i]?.arrivalTime;
      const durationToNext = durationsReversed[i] || 0;
      const arrivalNext = addSeconds(arrivalCur, durationToNext);
      stationsReversed[i + 1].arrivalTime = arrivalNext;
    }
  }

  const metadataToProcess: IRideMetadata = {
    ...(ride.metadata || {}),
    manualPrice: undefined,
    priceDetails: undefined,
    routePolyline: undefined,
    distances: distancesReversed,
    durations: durationsReversed,
    stationIds: stationsIdsReversed,
    stationsInfo: stationsReversed,
    passengersInfo: passengersInfoReversed,
    passengerIds: passengerIdsReversed,
  };

  const metadataConverted = normalizeRideMetadata(metadataToProcess, {
    direction,
  });
  const rideConverted: IRideDto = {
    ...ride,
    direction: direction,
    metadata: metadataConverted,
  };

  const isIndependentDayAdjustment = Boolean(
    originalRide.metadata?.hasIndependentDayAdjustment ||
    originalRide.rideTypeCategory ===
      RideTypeCategoryEnum.IndependentDayAdjustment,
  );

  if (isIndependentDayAdjustment) {
    rideConverted.rideStart = originalRide.rideStart;
    rideConverted.rideEnd = originalRide.rideEnd;

    if (rideConverted.metadata && originalRide.rideStart) {
      rideConverted.metadata = {
        ...rideConverted.metadata,
        stationsInfo: rebaseStationsArrivalTimesToUtcDate({
          stations: rideConverted.metadata.stationsInfo,
          targetDate: originalRide.rideStart,
          timezone,
        }),
      };
    }
  } else if (stationsReversed.length) {
    const firstStationTime = stationsReversed.at(0)?.arrivalTime;

    if (firstStationTime) {
      rideConverted.rideStart = firstStationTime;
    }

    const lastStationTime = stationsReversed.at(-1)?.arrivalTime;

    if (lastStationTime) {
      rideConverted.rideEnd = lastStationTime;
    }
  }

  if (rideConverted.metadata?.passengersInfo?.length) {
    for (const passenger of rideConverted.metadata.passengersInfo) {
      swapPassengerDirections(passenger);
    }
  }

  rideConverted.price = undefined;
  rideConverted.priceRateId = undefined;
  rideConverted.shiftTime = undefined;
  rideConverted.timeLate = undefined;
  rideConverted.statusLate = undefined;
  rideConverted.realStatusRideStartByGps = undefined;
  rideConverted.priceRateMaxDurationInMinutes = undefined;
  rideConverted.priceRateMaxBoardingSegmentDurationInMinutes = undefined;
  rideConverted.originStationId = originalRide.destinationStationId;
  rideConverted.originStationName = originalRide.destinationStationName;
  rideConverted.destinationStationId = originalRide.originStationId;
  rideConverted.destinationStationName = originalRide.originStationName;
  rideConverted.originAreaId = originalRide.destinationAreaId;
  rideConverted.destinationAreaId = originalRide.originAreaId;

  return rideConverted;
};

export const rideHasPassengersWithBookings = (
  rideDto: Partial<IRideDto>,
): boolean => {
  return !!rideDto?.metadata?.passengersInfo?.some(el => !!el.bookingId);
};

export const getFirstAddedStationArrivalTime = ({
  direction,
  rideStart,
  rideEnd,
}: Partial<IRideDto>): TDateISO | undefined => {
  return direction === DirectionEnum.PickUp ? rideEnd : rideStart;
};

export const getLastAddedStationArrivalTime = ({
  direction,
  rideStart,
  rideEnd,
  metadata,
}: Partial<IRideDto>): TDateISO | undefined => {
  const stations = metadata?.stationsInfo;

  if (!stations?.length) {
    return getFirstAddedStationArrivalTime({ direction, rideStart, rideEnd });
  }

  const stationsWithTime = stations.filter(
    s => !isStationRouteWaypointStation(s) && isValidDateTime(s.arrivalTime),
  );
  return stationsWithTime?.at(-1)?.arrivalTime;
};

export const isRideStationsOrderSame = (
  a: IStationInfoBase[] | undefined,
  b: IStationInfoBase[] | undefined,
): boolean => {
  const areArraysEqual = areArraysExactlySame(
    a?.map?.(getStationComposedId) ?? [],
    b?.map?.(getStationComposedId) ?? [],
  );
  return areArraysEqual;
};

export const passengerBoardedComputed = (
  passenger: IPassengerInfo,
): BoardingStateEnum => {
  if (passenger.boardedStatus) {
    return passenger.boardedStatus;
  }

  if (passenger.boardedAt) {
    return BoardingStateEnum.Boarded;
  }

  return BoardingStateEnum.Unknown;
};

export const resolveRideEditFormCreatedBy = (
  rideDto: Partial<IRideDto> | undefined,
  t: TFunction,
): string => {
  if (rideDto?.userName) {
    return rideDto?.userName;
  }

  if (rideDto?.userId) {
    return String(rideDto?.userId);
  }

  return t("user.syncerLegacyUser");
};

export const getRideBaseDate = (
  ride: Partial<IRideDto>,
): TDateISO | undefined => {
  return ride.direction === DirectionEnum?.PickUp
    ? ride.rideEnd
    : ride.rideStart;
};

export enum FreezeTimeErrorType {
  freezeTimeToCreateRide = "freezeTimeToCreateRide",
  freezeTimeToChangeRide = "freezeTimeToChangeRide",
  freezeTimeToCancelRide = "freezeTimeToCancelRide",
}

export const freezeTimeError = (
  type: FreezeTimeErrorType,
  params?: Partial<Record<string, SoftValidationErrorAdditionalValues>>,
): SimpleResult<unknown> =>
  createErrorResultExt({
    translationKey: errors.softValidation[type],
    translationParams: params as TTranslationParams,
  });

export const freezeTimeToCreateRideError = (
  params?: Partial<Record<string, SoftValidationErrorAdditionalValues>>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): SimpleResult<any> =>
  freezeTimeError(FreezeTimeErrorType.freezeTimeToCreateRide, params);

export const freezeTimeToChangeRideError = (
  params?: Partial<Record<string, SoftValidationErrorAdditionalValues>>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): SimpleResult<any> =>
  freezeTimeError(FreezeTimeErrorType.freezeTimeToChangeRide, params);

export const freezeTimeToCancelRideError = (
  params?: Partial<Record<string, SoftValidationErrorAdditionalValues>>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): SimpleResult<any> =>
  freezeTimeError(FreezeTimeErrorType.freezeTimeToCancelRide, params);

export const addMissingTimesStationsInfo = (
  stationsInfo: IStationInfo[],
  rideDto: Partial<IRideDto>,
): void => {
  stationsInfo?.forEach((s, i) => {
    if (isValidDateTime(s.arrivalTime)) {
      return;
    }

    if (i === 0) {
      s.arrivalTime = rideDto?.rideStart || dayjs().toISOString();
    } else {
      s.arrivalTime = stationsInfo?.[i - 1].arrivalTime;
    }
  });
};

export const getRidePassengerTooltipContent = (
  firstName: string | undefined,
  lastName: string | undefined,
  code: string | number | undefined,
  id: number,
  companionLabel?: string,
): string => {
  const fullName = `${firstName || ""} ${lastName || ""}`;
  const details = code ? `#${code}` : `#${NO_VALUE_DISPLAY},id:${id}`;
  const base = `${fullName} (${details})`;
  return companionLabel ? `${base}\n${companionLabel}` : base;
};

export interface IGetRidePassengerKanbanTooltipContentArgs {
  passenger: Pick<
    IPassengerInfo,
    "id" | "firstName" | "lastName" | "code" | "siteId" | "unitId" | "unitName"
  >;
  companionLabel?: string;
  rideSiteId?: number | null;
  rideSiteName?: string;
  siteLabel?: string;
  unitLabel?: string;
  rideSiteLabel?: string;
}

/** Plain-text kanban passenger tooltip (site, unit, ride-site mismatch). */
export const getRidePassengerKanbanTooltipContent = ({
  passenger,
  companionLabel,
  rideSiteId,
  rideSiteName,
  siteLabel,
  unitLabel,
  rideSiteLabel,
}: IGetRidePassengerKanbanTooltipContentArgs): string => {
  const lines = [
    getRidePassengerTooltipContent(
      passenger.firstName,
      passenger.lastName,
      passenger.code,
      passenger.id,
      companionLabel,
    ),
  ];

  const siteText = formatPassengerTooltipEntityWithId({
    id: passenger.siteId,
  });
  if (siteText && siteLabel) {
    let siteLine = `${siteLabel}: ${siteText}`;
    const rideSiteTooltipLabel = formatPassengerTooltipEntityWithId({
      name: rideSiteName,
      id: rideSiteId,
    });
    if (
      isPassengerRideSiteMismatch({
        passengerSiteId: passenger.siteId,
        rideSiteId,
      }) &&
      rideSiteTooltipLabel &&
      rideSiteLabel
    ) {
      siteLine += ` ${PASSENGER_RIDE_SITE_MISMATCH_EMOJI} (${rideSiteLabel}: ${rideSiteTooltipLabel})`;
    }
    lines.push(siteLine);
  }

  const unitText = formatPassengerTooltipEntityWithId({
    name: passenger.unitName,
    id: passenger.unitId,
  });
  if (unitText && unitLabel) {
    lines.push(`${unitLabel}: ${unitText}`);
  }

  return lines.join("\n");
};

export const extractRideAreasSnapshot = (
  rideDto: Partial<IRideDto> | undefined,
): IRideAreasSnapshot | null => {
  if (!rideDto?.metadata?.stationsInfo?.length) {
    return null;
  }

  const { first, last } = getRealRouteEndpoints(rideDto.metadata.stationsInfo);
  const firstStationAreaId = first?.areaId;
  const lastStationAreaId = last?.areaId;

  const snapshot: IRideAreasSnapshot = {
    areaFromId: firstStationAreaId,
    areaToId: lastStationAreaId,
  };
  return snapshot;
};

export const checkIsAreaChangedByUi = ({
  metadataComputed,
  ridePreviousAreasSnapshot,
}: {
  metadataComputed: IRideMetadata | undefined;
  ridePreviousAreasSnapshot: IRideAreasSnapshot | null;
}): boolean | undefined => {
  if (!metadataComputed?.stationsInfo?.length) {
    return undefined;
  }
  if (!ridePreviousAreasSnapshot) {
    return undefined;
  }

  const { first, last } = getRealRouteEndpoints(metadataComputed.stationsInfo);
  const firstStationAreaId = first?.areaId;
  const lastStationAreaId = last?.areaId;

  const hasAreaChanged =
    firstStationAreaId !== ridePreviousAreasSnapshot.areaFromId ||
    lastStationAreaId !== ridePreviousAreasSnapshot.areaToId;

  if (hasAreaChanged) {
    Log.debug(
      "Ride area change detected,areas before:",
      ridePreviousAreasSnapshot,
      "after change:",
      { from: firstStationAreaId, to: lastStationAreaId },
    );
  }

  return hasAreaChanged || undefined;
};

export const checkIsPassengersChangedByUi = ({
  metadataComputed,
  ridePreviousMetadataSnapshot,
}: {
  metadataComputed: IRideMetadata | undefined;
  ridePreviousMetadataSnapshot: IRideMetadata | null;
}): boolean | undefined => {
  const hasPassengersChanged =
    (metadataComputed?.passengerIds?.length || 0) !==
    (ridePreviousMetadataSnapshot?.passengerIds?.length || 0);
  return hasPassengersChanged || undefined;
};

export interface IRidesMinMaxDateRange {
  minDate: TDateISO | undefined;
  maxDate: TDateISO | undefined;
}

export const getRidesMinFirstMaxLastDate = (
  rides: Partial<IRideDto>[],
): IRidesMinMaxDateRange => {
  if (!rides.length) {
    const emptyRange: IRidesMinMaxDateRange = {
      minDate: undefined,
      maxDate: undefined,
    };
    return emptyRange;
  }

  const rideStarts = new Set<number>();
  const rideEnds = new Set<number>();

  for (const ride of rides) {
    if (isValidDateTime(ride.rideStart)) {
      rideStarts.add(dayjs(ride.rideStart).valueOf());
    }
    if (isValidDateTime(ride.rideEnd)) {
      rideEnds.add(dayjs(ride.rideEnd).valueOf());
    }
  }

  const minDate = rideStarts.size
    ? (new Date(Math.min(...rideStarts)).toISOString() as TDateISO)
    : undefined;
  const maxDate = rideEnds.size
    ? (new Date(Math.max(...rideEnds)).toISOString() as TDateISO)
    : undefined;

  const dateRange: IRidesMinMaxDateRange = {
    minDate,
    maxDate,
  };
  return dateRange;
};
